import { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { Spinner } from 'react-bootstrap';
import ClientSelect from '../../components/select/ClientSelect';
import { IClient } from '../../utils/types/IClient';
import { useAuth } from '../../utils/context/AuthContext';
import PdvSelect from '../../components/select/PdvSelect';
import { IPointDeVente } from '../../utils/types/IPointDeVente';
import PaiementGrid from '../../components/grid/PaiementGrid';
import { Helmet } from 'react-helmet-async';
import { ReglementClientDTO } from '../../utils/types/objects/ReglementClientDTO';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const Paiement = () => {
    const { user } = useAuth();

    // ---

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [selectedPdvId, setSelectedPdvId] = useState<number | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [numRecu, setNumRecu] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: pointsDeVente = [],
        isLoading: isLoadingPointsDeVente,
        refetch: refetchPointsDeVente,
        isError: isPointsDeVenteError,
        error: pointsDeVenteError,
    } = useQuery<IPointDeVente[]>({
        queryKey: ['pointsDeVente'],
        queryFn: async () => {
            const res = await apiClient.get('/api/pdv');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des points de vente' },
        enabled: !!user?.adminOrCommercial,
        staleTime: 1000 * 60 * 1, // 1 min
    });

    const {
        data: paiements = [],
        isLoading: isLoadingPaiements,
        refetch: refetchPaiements,
        isError: isPaiementsError,
        error: paiementsError,
    } = useQuery<ReglementClientDTO[]>({
        queryKey: [
            'paiements',
            {
                customerCardCode: selectedCardCode,
                idPointDeVente: selectedPdvId,
                dateDebut: startDate,
                dateFin: endDate,
                receiptNo: numRecu,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/paiements/getData', {
                params: {
                    customerCardCode: selectedCardCode,
                    idPointDeVente: selectedPdvId,
                    dateDebut: startDate,
                    dateFin: endDate,
                    receiptNo: numRecu,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des paiements' },
        enabled: false,
    });

    // ---

    const handleLoadPointsDeVente = useCallback(() => {
        if (!user?.adminOrCommercial) return;
        refetchPointsDeVente();
    }, [user, refetchPointsDeVente]);

    const handleLoadPaiements = useCallback(() => {
        if (!selectedCardCode) return;
        refetchPaiements();
    }, [selectedCardCode, refetchPaiements]);

    // ---

    useEffect(() => {
        if (clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
            handleLoadPaiements();
        }
    }, [clients, handleLoadPaiements]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isPointsDeVenteError, error: pointsDeVenteError, title: 'Erreur lors du chargement des points de vente', refetch: handleLoadPointsDeVente },
            { isError: isPaiementsError, error: paiementsError, title: 'Erreur lors du chargement des paiements', refetch: handleLoadPaiements },
        ],
        [isClientsError, clientsError, refetchClients, isPointsDeVenteError, pointsDeVenteError, handleLoadPointsDeVente, isPaiementsError, paiementsError, handleLoadPaiements],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Paiements :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Paiements</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none"></div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <div>
                                            <div className="row">
                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label htmlFor="SelectedCardCode">Client *</label>
                                                    <ClientSelect clients={clients} isClientsLoading={isLoadingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                                </div>

                                                <div className="col-6 col-sm-3 col-lg-2">
                                                    <label htmlFor="DateDebut">Du</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={startDate ?? ''}
                                                        onChange={(e) => {
                                                            setStartDate(e.target.value);
                                                        }}
                                                    />
                                                </div>

                                                <div className="col-6 col-sm-3 col-lg-2">
                                                    <label htmlFor="DateFin">Au</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={endDate ?? ''}
                                                        onChange={(e) => {
                                                            setEndDate(e.target.value);
                                                        }}
                                                    />
                                                </div>

                                                {user?.adminOrCommercial && (
                                                    <>
                                                        <div className="col-12 col-sm-6 col-lg-3">
                                                            <label htmlFor="SelectedCardCode">Station</label>
                                                            <PdvSelect pointsDeVente={pointsDeVente} isPointsDeVenteLoading={isLoadingPointsDeVente} selectedPdvId={selectedPdvId} setSelectedPdvId={setSelectedPdvId} />
                                                        </div>

                                                        <div className="col-6 col-sm-3 col-lg-2">
                                                            <label htmlFor="DateFin">No Reçu</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={numRecu ?? ''}
                                                                onChange={(e) => {
                                                                    setNumRecu(e.target.value);
                                                                }}
                                                                placeholder="Numéro Reçu"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="button" className="btn btn-default" disabled={isLoadingPaiements} onClick={handleLoadPaiements}>
                                                    {isLoadingPaiements ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        <PaiementGrid paiements={paiements} />
                    </div>
                </div>
            </div>
        </>
    );
};

Paiement.Layout = DefaultLayout;

export default Paiement;
